import produce from 'immer';
import { match, P } from 'ts-pattern';
import {
  ApiMasterDataQueryResponse,
  ApiMetricQueryResponse,
  ApiMetricQueryResponseDataPoint,
} from '../../../common/api/api-interfaces';
import {
  BenchmarkLocations,
  getBenchmarkTypesTranslations,
} from '../../../common/benchmark/benchmark-store';
import { Benchmark } from '../../../common/benchmark/types';
import {
  Granularity,
  Months,
} from '../../../common/date-manager/date-manager-constants';
import {
  DateManagerService,
  dateManagerService,
} from '../../../common/date-manager/date-manager-service';
import { getStartOfFiscalYear } from '../../../common/date-manager/date-manager-utils';
import { TimeSliderConfig } from '../../../common/filter/filter-store';
import { getDatesInRange, startOfDate } from '../../../common/filter/utils';
import {
  MetricGroupId,
  MetricId,
} from '../../../common/graphql/generated/graphql-sdk';
import { TimeSliderStore } from '../../../common/timeslider/timeslider-store';
import { enumKeys } from '../../../common/utilFunctions/pure-utils';
import { getValue, percentage } from '../../../common/utilFunctions/utils';
import { MetricDetails } from '../../../common/v2/api/metrics/types';
import { FilterTypes } from '../../../common/v2/common/components/filter/filterbar/types';
import { getFiscalQuarter } from '../../../common/v2/date/date-utils';
import {
  DataTypes,
  EmployeeDataFields,
  FinancialDataFields,
} from '../../../common/v2/types';
import { getTranslatedTableNameFromKey } from './translations';
import {
  CalendarGranularityConfig,
  DELTA,
  DELTA_PERCENTAGE,
  FinancialGranularityConfig,
  GranularityConfig,
  PbsCellData,
  PbsServiceResponseData,
  PERIOD_TOTAL,
  ResponseData,
  ResponseDataMetaData,
  SQLTableRowData,
  TimeSlice,
  ValueByDate,
} from './types';

export const executeSqlQuery = async (
  metricDetails: MetricDetails[],
  selectedBenchmark: Benchmark | null,
  overTimePromise: () => Promise<
    ApiMasterDataQueryResponse | ApiMetricQueryResponse
  >,
  totalPeriodPromise: () => Promise<
    ApiMasterDataQueryResponse | ApiMetricQueryResponse
  >,
  benchmarkPromise:
    | (() => Promise<ApiMasterDataQueryResponse | ApiMetricQueryResponse>)
    | null
): Promise<ResponseData> => {
  if (metricDetails.length === 0) {
    return Promise.resolve({ values: {} });
  }
  return Promise.all([
    overTimePromise(),
    totalPeriodPromise(),
    ...(benchmarkPromise ? [benchmarkPromise()] : []),
  ]).then(([overTimeResult, periodTotalResult, benchmarkResult]) => {
    let responseData: ResponseData = { values: {} };
    const meta: ResponseDataMetaData = {};
    metricDetails.map((dim) => {
      responseData.values[dim.metricGroupId] =
        responseData.values[dim.metricGroupId] || {};
      overTimeResult.dataPoints?.forEach((dp) => {
        const versionId = getValue(dp.dimensions, {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.VERSION_ID,
        });
        dp.measures.forEach((m) => {
          if (m.property === dim.id) {
            if ((dp as ApiMetricQueryResponseDataPoint).meta) {
              if (!meta[dim.metricGroupId]) {
                meta[dim.metricGroupId] = {};
              }
              if (!meta[dim.metricGroupId][dim.id]) {
                meta[dim.metricGroupId][dim.id] = {};
              }
              meta[dim.metricGroupId][dim.id].overtime = {
                metricSql: (dp as ApiMetricQueryResponseDataPoint).meta
                  .metricSql,
                metricGroupId: dim.metricGroupId,
                metricId: dim.id,
              };
            }
            if (responseData.values[dim.metricGroupId]) {
              // Don't know why these ts errors are happening here
              //@ts-ignore
              responseData.values[dim.metricGroupId][versionId] =
                //@ts-ignore
                responseData.values[dim.metricGroupId][versionId] || {};
              //@ts-ignore
              responseData.values[dim.metricGroupId][versionId][dim.id] =
                m?.value;
            }
          }
        });
      });
      periodTotalResult.dataPoints?.forEach((dp) => {
        dp.measures.forEach((m) => {
          if (m.property === dim.id) {
            if ((dp as ApiMetricQueryResponseDataPoint).meta) {
              if (!meta[dim.metricGroupId]) {
                meta[dim.metricGroupId] = {};
              }
              if (!meta[dim.metricGroupId][dim.id]) {
                meta[dim.metricGroupId][dim.id] = {};
              }
              meta[dim.metricGroupId][dim.id].periodTotal = {
                metricSql: (dp as ApiMetricQueryResponseDataPoint).meta
                  .metricSql,
                metricGroupId: dim.metricGroupId,
                metricId: dim.id,
              };
            }
            if (responseData.values[dim.metricGroupId]) {
              // Don't know why these ts errors are happening here
              //@ts-ignore
              responseData.values[dim.metricGroupId][PERIOD_TOTAL] =
                //@ts-ignore
                responseData.values[dim.metricGroupId][PERIOD_TOTAL] || {};
              //@ts-ignore
              responseData.values[dim.metricGroupId][PERIOD_TOTAL][dim.id] =
                m.value;
            }
          }
        });
      });

      if (selectedBenchmark) {
        benchmarkResult?.dataPoints?.forEach((dp) => {
          dp.measures.forEach((m) => {
            if (m.property === dim.id) {
              if ((dp as ApiMetricQueryResponseDataPoint).meta) {
                if (!meta[dim.metricGroupId]) {
                  meta[dim.metricGroupId] = {};
                }
                if (!meta[dim.metricGroupId][dim.id]) {
                  meta[dim.metricGroupId][dim.id] = {};
                }
                meta[dim.metricGroupId][dim.id].benchmark = {
                  metricSql: (dp as ApiMetricQueryResponseDataPoint).meta
                    .metricSql,
                  metricGroupId: dim.metricGroupId,
                  metricId: dim.id,
                };
              }
              if (responseData.values[dim.metricGroupId]) {
                // Don't know why these ts errors are happening here
                //@ts-ignore
                responseData.values[dim.metricGroupId][
                  selectedBenchmark.value
                ] =
                  //@ts-ignore
                  responseData.values[dim.metricGroupId][
                    selectedBenchmark.value
                  ] || {};
                //@ts-ignore
                responseData.values[dim.metricGroupId][selectedBenchmark.value][
                  dim.id
                ] = m.value;
              }
            }
          });
        });
      }
    });
    responseData.meta = meta;
    return responseData;
  });
};

export const getPeriod = (
  d: string,
  granularity: Granularity,
  financialYearStartMonth: Months,
  providedDateManagerService: DateManagerService = dateManagerService.getInstance()
): string => {
  const { parseApiDate } = providedDateManagerService;
  const date = parseApiDate(d);
  const year: number = date.year();
  switch (granularity) {
    case Granularity.MONTH:
      return providedDateManagerService.formatDateApi(date);
    case Granularity.QUARTER:
      return year + '-' + date.quarter();
    case Granularity.FINQUARTER:
      const fiscalYear = getStartOfFiscalYear(
        date,
        financialYearStartMonth
      ).year();
      const fiscalQuarter = getFiscalQuarter(
        date.valueOf(),
        financialYearStartMonth
      );
      return fiscalYear + '-' + fiscalQuarter;
    case Granularity.YEAR:
      return date.format('YYYY');
    case Granularity.FINYEAR:
      return getStartOfFiscalYear(date, financialYearStartMonth).format('YYYY');
    default:
      throw new Error(`Granularity ${granularity} not supported`);
  }
};

export const getGranularityConfig = (
  timeSliderConfig: TimeSliderConfig
): GranularityConfig => {
  return match(timeSliderConfig)
    .with(
      {
        granularity: P.union(
          Granularity.DAY,
          Granularity.WEEK,
          Granularity.MONTH,
          Granularity.YEAR,
          Granularity.QUARTER
        ),
      },
      (conf) => {
        return { granularity: conf.granularity } as CalendarGranularityConfig;
      }
    )
    .with(
      { granularity: P.union(Granularity.FINYEAR, Granularity.FINQUARTER) },
      (conf) => {
        const financialYearStartMonth = timeSliderConfig.firstMonthOfYear;
        return {
          granularity: conf.granularity,
          financialYearStartMonth,
        } as FinancialGranularityConfig;
      }
    )
    .exhaustive();
};

export const getTimeSlicesAccToTimeSliderConfig = (
  // TODO: not sure where to put this function
  timeSliderConfig: TimeSliderConfig
): TimeSlice[] => {
  const { formatDateApi, parseApiDate, formatDate } =
    dateManagerService.getInstance();
  const endOfSliceDates = getDatesInRange(timeSliderConfig);
  const { granularity, firstMonthOfYear } = timeSliderConfig;
  const timeSlices: TimeSlice[] = endOfSliceDates.map((endDate) => {
    const sliceStart = startOfDate(
      parseApiDate(endDate),
      granularity,
      firstMonthOfYear
    );
    const sliceEnd = parseApiDate(endDate);
    const slice: TimeSlice = {
      dateRange: [formatDateApi(sliceStart), formatDateApi(sliceEnd)],
      label: formatDate(sliceStart, granularity),
    };
    return slice;
  });
  return timeSlices;
};

export const formatPeriodConfigDate =
  (granularityConfig: GranularityConfig) =>
  (date: string): string => {
    return match(granularityConfig)
      .with(
        { granularity: P.union(Granularity.MONTH, Granularity.YEAR) },
        (value) => getPeriod(date, value.granularity, Months.January)
      )
      .with(
        { granularity: P.union(Granularity.FINQUARTER, Granularity.FINYEAR) },
        (value) =>
          getPeriod(date, value.granularity, value.financialYearStartMonth)
      )
      .otherwise(() => {
        throw new Error(
          `Granularity ${granularityConfig.granularity} not supported`
        );
      });
  };

export const getHeaderObjs = (
  timeSliderConfig: TimeSliderConfig,
  selectedBenchmark: Benchmark | null,
  timeSliderStore: TimeSliderStore
) => {
  const fixedHeaderObjs = [
    {
      label: getTranslatedTableNameFromKey(PERIOD_TOTAL),
      value: PERIOD_TOTAL,
    },
  ];
  const benchmarkHeaderObjs = [];
  if (selectedBenchmark) {
    benchmarkHeaderObjs.push(
      {
        label: getBenchmarkTypesTranslations(
          selectedBenchmark.value,
          BenchmarkLocations.HEADER,
          timeSliderStore
        ),
        value: selectedBenchmark.value,
      },
      {
        label: getTranslatedTableNameFromKey(DELTA),
        value: DELTA,
      },
      {
        label: getTranslatedTableNameFromKey(DELTA_PERCENTAGE),
        value: DELTA_PERCENTAGE,
      }
    );
  }

  const { formatApiDateToNewFormat } = dateManagerService.getInstance();
  const timeHeaderObjs = getTimeSlicesAccToTimeSliderConfig(
    timeSliderConfig
  ).map(({ label, dateRange }) => {
    return {
      label,
      value: label,
      subVal:
        timeSliderConfig.granularity !== Granularity.MONTH
          ? `${formatApiDateToNewFormat(dateRange[0], Granularity.MONTH)} - ${formatApiDateToNewFormat(
              dateRange[1],
              Granularity.MONTH
            )}`
          : undefined,
    };
  });

  return {
    fixedHeaderObjs,
    timeHeaderObjs,
    benchmarkHeaderObjs,
  };
};

export const getPbsServiceResponseData = (
  metricData: ValueByDate,
  timeSlices: TimeSlice[],
  dateFormatter: (date: string) => string,
  selectedBenchmark: Benchmark | null
): PbsServiceResponseData => {
  const PbsServiceResponseData: PbsServiceResponseData = {
    fixedHeadersData: {
      [PERIOD_TOTAL]: metricData[PERIOD_TOTAL],
    },
    timeHeadersData: timeSlices.map(({ label, dateRange }) => {
      const key = dateFormatter(dateRange[1]);
      return {
        label,
        dateRange,
        data: metricData[key],
      };
    }),
  };
  const PbsServiceResponseDataWithBenchmark = produce(
    PbsServiceResponseData,
    (draft) => {
      if (selectedBenchmark) {
        const periodTotalData = metricData[PERIOD_TOTAL] as Record<
          MetricId,
          number | null
        >;
        const benchmarkData = metricData[selectedBenchmark.value] as Record<
          MetricId,
          number | null
        >;
        const { deltaData, deltaPercentageData } = getDeltaData(
          periodTotalData,
          benchmarkData
        ) as {
          deltaData: Record<MetricId, number | null>;
          deltaPercentageData: Record<MetricId, number | null>;
        };
        draft = {
          ...draft,
          benchmarkHeadersData: {
            [selectedBenchmark.value]: benchmarkData,
            [DELTA]: deltaData,
            [DELTA_PERCENTAGE]: deltaPercentageData,
          },
        };
      }
      return draft;
    }
  );
  return PbsServiceResponseDataWithBenchmark;
};

export const getFormattedPbsTableRowData = (
  metricGroupId: MetricGroupId,
  metricData: ValueByDate,
  timeSlices: TimeSlice[],
  dateFormatter: (date: string) => string,
  selectedBenchmark: Benchmark | null
): SQLTableRowData => {
  const formattedData: SQLTableRowData = {
    metricGroupId,
    data: {
      [metricGroupId]: getPbsServiceResponseData(
        metricData,
        timeSlices,
        dateFormatter,
        selectedBenchmark
      ),
    },
  };
  return formattedData;
};

export const getDeltaData = (data1: PbsCellData, data2: PbsCellData) => {
  const deltaData = Object.fromEntries(
    enumKeys(data1).map((k) => {
      const isNull = data1[k] === null || data2[k] === null;
      return [k, isNull ? null : (data1[k] as number) - (data2[k] as number)];
    })
  ) as typeof data1;
  const deltaPercentageData = Object.fromEntries(
    enumKeys(data1).map((k) => {
      const isNull = data1[k] === null || data2[k] === null;
      return [
        k,
        isNull
          ? null
          : percentage(
              (data1[k] as number) - (data2[k] as number),
              data2[k] as number
            ),
      ];
    })
  ) as typeof data1;
  // TODO: Delta and benchmark options need to be handled more scalably once I add support
  // for more benchmark types
  return {
    deltaData,
    deltaPercentageData,
  };
};

export const allowedFiltersForFinancialMetrics = <T extends FilterTypes>(
  filters: T[]
): T[] =>
  filters
    .filter((f) =>
      match(f.values)
        .with(
          P.array(P.string),
          (values) =>
            (f.property === EmployeeDataFields.ORGANIZATION &&
              values[0].length === 1) ||
            (f.property === EmployeeDataFields.FUNCTION &&
              values[0].length === 1)
        )
        .otherwise(() => false)
    )
    .map((f) => {
      return {
        dataType: DataTypes.FINANCIALMETRICS,
        operation: f.operation,
        property:
          f.property === EmployeeDataFields.ORGANIZATION
            ? FinancialDataFields.ORGANIZATION_LEVEL_1
            : FinancialDataFields.FUNCTION_LEVEL_1,
        values: f.values[0],
      };
    }) as T[];
